import { ProductDetailedMeta } from 'models/ProductMeta';
import styles from './deliveryInformation.scss';
import cn from 'classnames';
import connect from 'components/Shared/connect';
import { isDetailedMeta, showDeliveryInfo, getStockMeta } from 'views/ProductPage/selector';
import { NO_ELEMENT, rm } from 'utils';
import { mapCarriageMethod } from 'components/Shared/Product/selector';
import { IconCheckmark } from 'components/Shared/Icon';
import { DeliveryEstimate } from 'views/ProductPage/productInfo/deliveryEstimate';
import { Stock } from 'views/ProductPage/productInfo/deliveryInformation/components/Stock';
import { SpecialPriceMeta } from 'views/ProductPage/productInfo/specialPriceMeta';
import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { hasRentalPriceSelector, selectShowDeliveryInfo } from 'components/Shared/Price/selector';
import { selectShouldUseIngrid } from 'views/CheckoutPage/selector';
import { NotifyMe } from 'views/ProductPage/productInfo/deliveryInformation/components/NotifyMe';

interface OwnProps {
  className?: string;
}

export interface MergedProps extends OwnProps {
  meta: ProductDetailedMeta | undefined;
  showDeliveryInfo: boolean;
}

const Checkmark = ({ children }: { children: ReactNode }) => (
  <>
    <IconCheckmark className={styles.checkmark} />
    {children}
  </>
);

export const _DeliveryInformation = ({ meta, showDeliveryInfo, className }: MergedProps) => {
  const isRentalProduct = useSelector(hasRentalPriceSelector);
  const shouldShowDeliveryInfo = useSelector(selectShowDeliveryInfo);

  if (!isDetailedMeta(meta) || !showDeliveryInfo) {
    return NO_ELEMENT;
  }

  return (
    <>
      <NotifyMe />
      <dl className={cn(styles.base, className)}>
        <dl>
          <dt>
            <Stock
              stockText={isRentalProduct ? rm('delivery_stock_rental_product') : meta.stock}
              stockStatus={meta.stockStatus}
              saleStatus={meta.saleStatus}
            />
            &nbsp;
            <DeliveryEstimate meta={meta} showDeliveryInfo={showDeliveryInfo && shouldShowDeliveryInfo} />
          </dt>
          {meta.isLowInStock && (
            <dd className={styles.lowInStock}>{rm('deliverystatus_resultstring_low_in_stock')}</dd>
          )}
        </dl>
        <DeliveryInfo
          showDeliveryInfo={showDeliveryInfo}
          meta={meta}
          isRentalProduct={isRentalProduct}
        />
        <ServiceCharge meta={meta} />
        <SpecialPriceMeta className={styles.serviceChargeInfo} />
      </dl>
    </>
  );
};

const ServiceCharge = ({ meta }: Pick<DeliveryInfoProps, 'meta'>) => {
  const shouldUseIngrid = useSelector(selectShouldUseIngrid);

  if (shouldUseIngrid) return NO_ELEMENT;

  return (
    <>
      {meta.showServiceChargeInfo && (
        <p className={styles.serviceChargeInfo}>{rm('Delivery_Service_Charge_Info')}</p>
      )}
    </>
  );
};

interface DeliveryInfoProps {
  meta: ProductDetailedMeta;
  showDeliveryInfo: boolean;
  isRentalProduct: boolean;
}
const DeliveryInfo = ({
  meta,
  showDeliveryInfo,
  isRentalProduct
}: DeliveryInfoProps): JSX.Element | null => {
  if (isRentalProduct) {
    return <strong>{rm('delivery_salestatus_rental')}</strong>;
  }

  if (showDeliveryInfo) {
    return (
      <div>
        <dt className={styles.definite}>
          <Checkmark>
            <strong>{mapCarriageMethod(meta.carriageMethod)} </strong>
          </Checkmark>
        </dt>
        &nbsp;
        <dd>{meta.deliveryCost}</dd>
      </div>
    );
  }

  return null;
};

export const DeliveryInformation = connect<MergedProps, OwnProps>(state => ({
  meta: getStockMeta(state),
  showDeliveryInfo: showDeliveryInfo(state.currentPage)
}))(_DeliveryInformation);
